import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import React, { useState } from "react";

import { getDefaultMonthRange } from "../../utils/monthRange";

const toMoment = (yearMonth) => (yearMonth ? moment(yearMonth, "YYYY-MM") : null);
const toYearMonth = (value) => (value && value.isValid() ? value.format("YYYY-MM") : "");

// Clicking anywhere on the field opens the picker, so the input is read-only
const MonthField = ({ label, value, minDate, maxDate, onChange, error }) => {
    const [open, setOpen] = useState(false);

    return (
        <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
            <DatePicker
                views={["year", "month"]}
                openTo="month"
                format="MMM YYYY"
                value={value}
                minDate={minDate}
                maxDate={maxDate}
                onChange={onChange}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                slotProps={{
                    textField: {
                        size: "small",
                        error,
                        onClick: () => setOpen(true),
                        inputProps: { readOnly: true },
                        // text-sm (0.875rem), matching the SingleSelectField used for Group By
                        sx: {
                            width: 150,
                            backgroundColor: "white",
                            "& .MuiInputBase-input": {
                                fontSize: "0.875rem",
                                cursor: "pointer",
                            },
                        },
                    },
                    // Make selectable months/years obvious vs. disabled ones in the popup
                    desktopPaper: {
                        sx: {
                            "& .MuiPickersMonth-monthButton, & .MuiPickersYear-yearButton": {
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                color: "#1f2937",
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: "#e0e7ff",
                                    borderColor: "#6366f1",
                                },
                                "&.Mui-selected": {
                                    backgroundColor: "#6366f1",
                                    borderColor: "#6366f1",
                                    color: "#fff",
                                    "&:hover": { backgroundColor: "#4f46e5" },
                                },
                                "&.Mui-disabled": {
                                    border: "1px solid transparent",
                                    color: "#cbd5e1",
                                    cursor: "not-allowed",
                                },
                            },
                        },
                    },
                }}
            />
        </div>
    );
};

const MonthRangePicker = ({ value, minMonth, onChange }) => {
    const [draft, setDraft] = useState(value);

    // Months are only complete once they end; cap selection at the previous month
    const maxMonth = toMoment(getDefaultMonthRange().endMonth);
    // Months before the earliest available data are not selectable
    const minDate = minMonth ? toMoment(minMonth) : undefined;

    // Apply immediately; invalid intermediate states (e.g., From past To) stay local
    const handleDraftChange = (next) => {
        setDraft(next);
        if (next.startMonth && next.endMonth && next.startMonth <= next.endMonth) {
            onChange(next);
        }
    };

    // Invalid ranges are never propagated, but the user should see why
    const isInvalidRange =
        Boolean(draft.startMonth && draft.endMonth) && draft.startMonth > draft.endMonth;

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <div>
                <div className="flex items-end gap-3">
                    <MonthField
                        label="From"
                        value={toMoment(draft.startMonth)}
                        minDate={minDate}
                        maxDate={maxMonth}
                        error={isInvalidRange}
                        onChange={(newValue) =>
                            handleDraftChange({ ...draft, startMonth: toYearMonth(newValue) })
                        }
                    />
                    <MonthField
                        label="To"
                        value={toMoment(draft.endMonth)}
                        minDate={minDate}
                        maxDate={maxMonth}
                        error={isInvalidRange}
                        onChange={(newValue) =>
                            handleDraftChange({ ...draft, endMonth: toYearMonth(newValue) })
                        }
                    />
                </div>
                {isInvalidRange && (
                    <p className="mt-1 text-xs text-red-600">
                        Start month must be on or before end month
                    </p>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default MonthRangePicker;
